import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import {
    BarChart3, Bell, CalendarDays, ChevronLeft, ChevronRight, FolderOpen,
    GraduationCap, Images, LayoutGrid, LogOut, Menu, MessageSquare,
    ScanFace, Shield, Smartphone, Users, X,
} from 'lucide-react';
import Footer from './Footer';
import { AuthContext } from '../../authContext';

const navForRole = (role) => {
    if (role === 'parent') {
        return [
            { to: '/dashboard', label: 'Home', Icon: LayoutGrid },
            { to: '/my-child', label: 'My child', Icon: ScanFace },
            { to: '/gallery', label: 'School gallery', Icon: Images },
            { to: '/notifications', label: 'Announcements', Icon: Bell },
            { to: '/events', label: 'Events', Icon: CalendarDays },
        ];
    }
    if (role === 'teacher') {
        return [
            { to: '/teacher/dashboard', label: 'Dashboard', Icon: LayoutGrid },
            { to: '/teacher/media', label: 'Media', Icon: Images },
            { to: '/photo-coverage', label: 'Photo coverage', Icon: BarChart3 },
            { to: '/meetings', label: 'Meetings', Icon: MessageSquare },
        ];
    }
    return [
        { to: '/admin', label: 'Command center', Icon: Shield, end: true },
        { to: '/admin/media', label: "Parents' gallery", Icon: Images },
        { to: '/admin/photo-matching', label: 'Photo matching', Icon: ScanFace },
        { to: '/photo-coverage', label: 'Photo coverage', Icon: BarChart3 },
        { to: '/admin/internal-media', label: 'Internal media', Icon: FolderOpen },
        { to: '/admin/parents', label: 'Parents', Icon: Users },
        { to: '/admin/teachers', label: 'Teachers', Icon: GraduationCap },
        { to: '/admin/events', label: 'Events', Icon: CalendarDays },
        { to: '/admin/notifications', label: 'Announcements', Icon: Bell },
        { to: '/admin/analytics', label: 'Analytics', Icon: BarChart3 },
        { to: '/admin/app-analytics', label: 'App analytics', Icon: Smartphone },
        { to: '/meetings', label: 'Meetings', Icon: MessageSquare },
    ];
};

const initials = (name) => String(name || 'A').trim().split(/\s+/).slice(0, 2).map((part) => part[0]).join('').toUpperCase();
const roleLabel = (role, level) => level === 'coordinator' ? 'Coordinator' : role?.replace('-', ' ') || 'Parent';

const AppShell = ({ children }) => {
    const { user, onLogout } = useContext(AuthContext);
    const location = useLocation();
    const role = user?.role || 'parent';
    const items = useMemo(() => navForRole(role), [role]);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [collapsed, setCollapsed] = useState(() => localStorage.getItem('abhyaas-nav-collapsed') === 'true');
    useEffect(() => { setDrawerOpen(false); }, [location.pathname]);
    useEffect(() => { localStorage.setItem('abhyaas-nav-collapsed', String(collapsed)); }, [collapsed]);

    const renderLinks = () => items.map(({ to, label, Icon, end }) => (
        <NavLink key={to} to={to} end={end} title={collapsed ? label : undefined} className={({ isActive }) => `abh-navlink${isActive ? ' active' : ''}`}>
            <span className="abh-navicon"><Icon size={19} /></span><span className="abh-navlabel">{label}</span>
        </NavLink>
    ));

    return (
        <div className={`abh-shell${collapsed ? ' nav-collapsed' : ''}`}>
            <AppShellStyles />
            {drawerOpen && <button className="abh-scrim" aria-label="Close menu" onClick={() => setDrawerOpen(false)} />}
            <aside className={`abh-sidebar${drawerOpen ? ' open' : ''}`}>
                <div className="abh-brand-row">
                    <Link to="/" className="abh-brand" aria-label="Abhyaas home"><img src="/logo.svg" alt="Abhyaas" /></Link>
                    <button className="abh-mobile-close" onClick={() => setDrawerOpen(false)} aria-label="Close navigation"><X size={18} /></button>
                </div>
                <div className="abh-workspace-label"><span>Workspace</span></div>
                <nav className="abh-nav" aria-label="Main navigation">{renderLinks()}</nav>
                <div className="abh-side-bottom">
                    <div className="abh-account">
                        <span className="abh-avatar">{initials(user?.name)}</span>
                        <span className="abh-account-copy"><strong>{user?.name || 'Abhyaas family'}</strong><small>{roleLabel(role, user?.level)}</small></span>
                    </div>
                    <div className="abh-utility-row">
                        <button onClick={() => onLogout?.()} aria-label="Log out" title="Log out"><LogOut size={18} /><span className="abh-util-label">Log out</span></button>
                    </div>
                </div>
                <button className="abh-collapse" onClick={() => setCollapsed((value) => !value)} aria-label={collapsed ? 'Expand navigation' : 'Collapse navigation'}>{collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}</button>
            </aside>

            <div className="abh-main-lane">
                <header className="abh-mobile-head">
                    <Link to="/" className="abh-mobile-brand"><img src="/logo.svg" alt="Abhyaas" /></Link>
                    <button className="abh-menu" onClick={() => setDrawerOpen(true)} aria-label="Open navigation"><Menu size={20} /></button>
                </header>
                <main className="abh-content">{children}</main>
                <Footer />
            </div>
        </div>
    );
};

const AppShellStyles = () => (
    <style>{`
        .abh-shell { min-height: 100vh; display: grid; grid-template-columns: 252px minmax(0,1fr); background: var(--paper); color: var(--ink-900); transition: grid-template-columns var(--dur) var(--ease-std); }
        .abh-shell.nav-collapsed { grid-template-columns: 82px minmax(0,1fr); }
        .abh-sidebar { position: sticky; top: 0; height: 100vh; z-index: 50; display: flex; flex-direction: column; padding: 18px 14px 15px; background: linear-gradient(180deg,#0c2117 0%,#081a12 100%); border-right: 1px solid var(--line); box-shadow: 16px 0 50px rgba(0,0,0,.16); }
        .abh-brand-row { height: 51px; display: flex; align-items: center; justify-content: space-between; padding: 0 4px 10px 2px; }
        .abh-brand { display: flex; align-items: center; overflow: hidden; min-width: 0; }
        .abh-brand img { height: 29px; width: auto; max-width: 170px; display: block; }
        .abh-workspace-label { height: 32px; display: flex; align-items: center; padding: 0 12px; color: var(--ink-300); font-size: 10px; line-height: 1; font-weight: 800; letter-spacing: .13em; text-transform: uppercase; overflow: hidden; white-space: nowrap; }
        .abh-nav { display: flex; flex-direction: column; gap: 3px; overflow-y: auto; overflow-x: hidden; padding: 4px 0 14px; scrollbar-width: thin; }
        .abh-navlink { position: relative; min-height: 44px; display: flex; align-items: center; gap: 11px; padding: 5px 11px; border-radius: 13px; color: var(--ink-700); text-decoration: none; font-size: 13px; font-weight: 650; white-space: nowrap; transition: background var(--dur-fast), color var(--dur-fast), transform var(--dur-fast); }
        .abh-navlink:hover { background: var(--surface-sunken); color: var(--ink-900); transform: translateX(2px); }
        .abh-navlink.active { background: var(--green-100); color: var(--green-800); font-weight: 750; }
        .abh-navlink.active::before { content: ''; position: absolute; left: -14px; top: 11px; bottom: 11px; width: 3px; border-radius: 0 5px 5px 0; background: var(--green-600); }
        .abh-navicon { width: 23px; height: 23px; display: grid; place-items: center; flex: none; }
        .abh-navlabel { overflow: hidden; text-overflow: ellipsis; }
        .abh-side-bottom { margin-top: auto; border-top: 1px solid var(--line); padding-top: 12px; }
        .abh-account { display: flex; align-items: center; gap: 10px; padding: 7px; min-width: 0; }
        .abh-avatar { width: 36px; height: 36px; border-radius: 12px; flex: none; display: grid; place-items: center; color: #fff; background: linear-gradient(145deg,#168f52,#0e3b2e); font: 800 12px var(--font-display); box-shadow: 0 6px 15px rgba(22,143,82,.22); }
        .abh-account-copy { min-width: 0; display: flex; flex-direction: column; line-height: 1.2; }
        .abh-account-copy strong { font-size: 12px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
        .abh-account-copy small { margin-top: 4px; color: var(--ink-500); font-size: 10px; text-transform: capitalize; }
        .abh-utility-row { display: grid; grid-template-columns: 1fr; gap: 6px; margin-top: 7px; }
        .abh-utility-row button, .abh-mobile-close, .abh-menu { min-height: 39px; border: 1px solid var(--line); border-radius: 11px; display: flex; align-items: center; justify-content: center; gap: 7px; background: var(--surface); color: var(--ink-700); cursor: pointer; font: 650 11px var(--font-body); }
        .abh-utility-row button:hover, .abh-menu:hover { background: var(--green-50); color: var(--green-800); border-color: var(--green-200); }
        .abh-collapse { position: absolute; right: -13px; top: 72px; width: 27px; height: 27px; display: grid; place-items: center; border: 1px solid var(--line); border-radius: 50%; background: var(--surface); color: var(--ink-500); box-shadow: var(--e2); cursor: pointer; }
        .abh-main-lane { min-width: 0; min-height: 100vh; display: flex; flex-direction: column; }
        .abh-content { flex: 1; max-width: 1460px; width: 100%; margin: 0 auto; padding: clamp(20px,3vw,38px); }
        .abh-mobile-head, .abh-mobile-close, .abh-scrim { display: none; }
        .abh-shell.nav-collapsed .abh-brand img { width: 41px; max-width: none; object-fit: cover; object-position: left; }
        .abh-shell.nav-collapsed .abh-workspace-label span, .abh-shell.nav-collapsed .abh-navlabel, .abh-shell.nav-collapsed .abh-account-copy, .abh-shell.nav-collapsed .abh-util-label { display: none; }
        .abh-shell.nav-collapsed .abh-navlink { justify-content: center; padding: 5px; }
        .abh-shell.nav-collapsed .abh-navlink:hover { transform: none; }
        .abh-shell.nav-collapsed .abh-account { justify-content: center; padding: 7px 0; }
        .abh-shell.nav-collapsed .abh-utility-row { grid-template-columns: 1fr; }
        [data-theme="dark"] .abh-navlink.active { background: var(--surface-sunken); color: var(--green-300); }
        @media (max-width: 920px) {
            .abh-shell, .abh-shell.nav-collapsed { display: block; }
            .abh-sidebar { position: fixed; left: 0; top: 0; width: min(292px,86vw); transform: translateX(-105%); transition: transform var(--dur) var(--ease-std); box-shadow: var(--e4); }
            .abh-sidebar.open { transform: none; }
            .abh-shell.nav-collapsed .abh-brand img { width: auto; max-width: 170px; object-fit: initial; }
            .abh-shell.nav-collapsed .abh-workspace-label span, .abh-shell.nav-collapsed .abh-navlabel, .abh-shell.nav-collapsed .abh-account-copy, .abh-shell.nav-collapsed .abh-util-label { display: initial; }
            .abh-shell.nav-collapsed .abh-navlink { justify-content: flex-start; padding: 5px 11px; }
            .abh-shell.nav-collapsed .abh-account { justify-content: flex-start; padding: 7px; }
            .abh-shell.nav-collapsed .abh-utility-row { grid-template-columns: 1fr; }
            .abh-mobile-close { display: grid; width: 38px; padding: 0; flex: none; }
            .abh-collapse { display: none; }
            .abh-mobile-head { height: 62px; position: sticky; top: 0; z-index: 35; display: flex; align-items: center; justify-content: space-between; padding: 9px 17px 9px 12px; background: rgba(255,255,255,.90); border-bottom: 1px solid var(--line); backdrop-filter: blur(14px); }
            [data-theme="dark"] .abh-mobile-head { background: rgba(15,36,25,.91); }
            .abh-mobile-brand img { height: 26px; display: block; }
            .abh-menu { width: 42px; padding: 0; }
            .abh-scrim { display: block; position: fixed; inset: 0; z-index: 45; border: 0; background: rgba(7,39,28,.55); backdrop-filter: blur(3px); }
            .abh-content { padding: 20px 16px 34px; }
        }
    `}</style>
);

export default AppShell;
